// Rendering a team member.
//
// `TeamMemberEntry` carries nullable `display_name` / `email` / `avatar_url`
// alongside the `user_id`. The nullability is the interesting part and it is
// permanent, not transitional: these ids come from GoTrue, API keys, service
// accounts, the debug user and `PUBLIC_USER_ID`, and only the first has an
// `auth.users` row to join against. A null name is therefore a fact about that
// principal, not a lookup that failed — which is exactly why the server widened
// the row instead of adding an endpoint that would have had to 404 at something
// completely ordinary.
//
// So: never treat null as an error, and never render a spinner or a retry for
// it. Fall through to the id.

import type { TeamMember } from '../../api/teams'

/** Characters of the id to show. Enough to distinguish, short enough to scan. */
const SHORT_ID_LENGTH = 8

/**
 * A stable, obviously-machine-generated label for a user id.
 *
 * Rendered monospace at the call site so it reads as a reference rather than as
 * a person's name. It does not slice the id into something name-shaped, and the
 * caller does not derive initials or a colour from it — inventing identity for
 * a principal that has none is worse than admitting there is none.
 */
export function shortUserId(userId: string): string {
  if (userId.length <= SHORT_ID_LENGTH) return userId
  return `${userId.slice(0, SHORT_ID_LENGTH)}\u2026`
}

/** What to show as the member's primary label, best available first. */
export function memberLabel(member: TeamMember): string {
  if (member.displayName !== null && member.displayName !== '') return member.displayName
  if (member.email !== null && member.email !== '') return member.email
  return shortUserId(member.userId)
}

/** True when the label is an id rather than a name, so it can be monospaced. */
export function memberLabelIsId(member: TeamMember): boolean {
  const hasName = member.displayName !== null && member.displayName !== ''
  const hasEmail = member.email !== null && member.email !== ''
  return !hasName && !hasEmail
}

/** The secondary line: the email, but only when it is not already the label. */
export function memberSecondaryLabel(member: TeamMember): string | null {
  const hasName = member.displayName !== null && member.displayName !== ''
  if (!hasName) return null
  return member.email !== null && member.email !== '' ? member.email : null
}
